import { useCallback, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Fixed-position catalog search menu (portaled to body).
 * Avoids overflow clipping inside section cards / PatientDetail.
 */
export function useSearchMenuPosition(anchorRef, open) {
  const [style, setStyle] = useState(null);

  const update = useCallback(() => {
    const el = anchorRef?.current;
    if (!el || !open) {
      setStyle(null);
      return;
    }
    const r = el.getBoundingClientRect();
    const gap = 6;
    const minWidth = Math.max(r.width, 260);
    const left = Math.min(
      Math.max(8, r.left),
      Math.max(8, window.innerWidth - minWidth - 8),
    );
    const spaceBelow = window.innerHeight - r.bottom - gap;
    const spaceAbove = r.top - gap;
    const openUp = spaceBelow < 220 && spaceAbove > spaceBelow;
    const maxHeight = Math.min(320, Math.max(140, openUp ? spaceAbove : spaceBelow));

    setStyle({
      position: "fixed",
      left,
      width: minWidth,
      zIndex: 10050,
      ...(openUp
        ? { bottom: window.innerHeight - r.top + gap, maxHeight }
        : { top: r.bottom + gap, maxHeight }),
    });
  }, [anchorRef, open]);

  useLayoutEffect(() => {
    if (!open) {
      setStyle(null);
      return undefined;
    }
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, update]);

  return style;
}

export function SearchMenuPortal({
  anchorRef,
  menuRef,
  open,
  children,
  className = "rounded-lg border border-slate-200 bg-white shadow-xl overflow-hidden",
}) {
  const style = useSearchMenuPosition(anchorRef, open);
  if (!open || !style || typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={menuRef}
      style={style}
      className={className}
      role="listbox"
    >
      <div className="h-full max-h-[inherit] overflow-auto">{children}</div>
    </div>,
    document.body,
  );
}
